import { useState, useMemo, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { RegisterToViewModal } from "@/components/public/RegisterToViewModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Carousel3D, Carousel3DItem } from "@/components/ui/carousel-3d";
import { ImpactStats } from "@/components/public/ImpactStats";
import { ImpactHighlights } from "@/components/public/ImpactHighlights";
import TestimonialsCarousel from "@/components/public/TestimonialsCarousel";
import { Brain, Award, Users, Sparkles, Building2, Play, ChevronRight, Star, CheckCircle, ArrowRight, Zap, Target, Lightbulb, TrendingUp, BookOpen, MessageCircle, Stethoscope, FolderKanban, Clock, Eye, Flame, Loader2, Lock, Video, FileText, Headphones, Layers } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLicensePrice } from "@/hooks/useLicensePrice";
import { useFeaturedNews } from "@/hooks/useNewsArticles";

import { useAuth } from "@/contexts/AuthContext";
import { SEO } from "@/components/SEO";
import { useForumThreads } from "@/hooks/useForum";
import { THREAD_CATEGORIES } from "@/data/forumData";
import { usePrograms } from "@/hooks/usePrograms";
import { usePublicRoutesEnabled } from "@/hooks/usePublicRoutesEnabled";
import OnboardingChecklistCard from "@/components/onboarding/OnboardingChecklistCard";
import { formatDistanceToNow, parseISO } from "date-fns";
import { es } from "date-fns/locale";

// Import real event images
import eventConferencia from "@/assets/events/lider-ia-conferencia.jpg";
import eventWorkshop from "@/assets/events/lider-ia-workshop.jpg";

// Expert photos
import expertHarold from "@/assets/experts/harold.png";
import expertAndres from "@/assets/experts/andres.png";
import expertKeythleen from "@/assets/experts/keythleen.png";
import expertJulian from "@/assets/experts/julian.png";

// Import news images
import aiFutureImg from "@/assets/news/ai-future.jpg";
import dataWorkshopImg from "@/assets/news/data-workshop.jpg";
import demoDayImg from "@/assets/news/demo-day.jpg";
import latamAutomationImg from "@/assets/news/latam-automation.jpg";
import promptingImg from "@/assets/news/prompting.jpg";
import leadershipImg from "@/assets/news/leadership.jpg";
import resourcesImg from "@/assets/news/resources.jpg";
const events3DData: Carousel3DItem[] = [{
  title: "Líder IA - Colombia 4.0",
  subtitle: "Bogotá, Colombia • 500+ asistentes",
  image: eventConferencia,
  badge: "Conferencia"
}, {
  title: "Sesión en Vivo Empresarial IA",
  subtitle: "Cartagena, Colombia • 200+ profesionales",
  image: eventWorkshop,
  badge: "Sesión en Vivo"
}, {
  title: "Líder IA - Colombia 4.0",
  subtitle: "Bogotá, Colombia • 500+ asistentes",
  image: eventConferencia,
  badge: "Conferencia"
}, {
  title: "Sesión en Vivo Empresarial IA",
  subtitle: "Cartagena, Colombia • 200+ profesionales",
  image: eventWorkshop,
  badge: "Sesión en Vivo"
}];

// Helper for content type icons/labels
const getContentIcon = (type: string | null) => {
  switch (type?.toLowerCase()) {
    case 'video': return <Video className="h-3 w-3" />;
    case 'reading': case 'lectura': return <FileText className="h-3 w-3" />;
    case 'audio': return <Headphones className="h-3 w-3" />;
    case 'infographic': case 'infografía': return <Layers className="h-3 w-3" />;
    default: return <BookOpen className="h-3 w-3" />;
  }
};

const getTypeLabel = (type: string | null) => {
  switch (type?.toLowerCase()) {
    case 'video': return 'Video';
    case 'reading': case 'lectura': return 'Lectura';
    case 'audio': return 'Audio';
    case 'infographic': case 'infografía': return 'Infografía';
    case 'interactive': case 'interactivo': return 'Interactivo';
    default: return type || 'Recurso';
  }
};

// fallback images for news without image_url
const newsFallbackImages = [aiFutureImg, dataWorkshopImg, demoDayImg, latamAutomationImg];

// fallback thumbnails for content cards (same set used in ContentPage)
const contentFallbackThumbnails = [
  'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1553028826-f4804a6dba3b?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=400&h=225&fit=crop',
  'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=400&h=225&fit=crop',
];


const PublicHomePage = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { label: proLabel, periodLabel: proPeriod } = useLicensePrice();
  const isLoggedIn = !!user;
  const showChecklist = isLoggedIn && !profile?.onboarding_checklist_dismissed_at;
  const { data: latestNews = [], isLoading: newsLoading } = useFeaturedNews(4);
  const { data: forumThreads = [], isLoading: forumLoading } = useForumThreads();
  const { data: allPrograms = [], isLoading: programsLoading } = usePrograms();
  const { enabled: routesEnabled } = usePublicRoutesEnabled();

  // Public content sample for visitors
  const [contents, setContents] = useState<any[]>([]);
  const [contentsLoading, setContentsLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [selectedContentName, setSelectedContentName] = useState<string | undefined>();

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data } = await supabase
        .from("content_bank")
        .select("id, name, content_type, category, duration, thumbnail_url, description")
        .eq("access_level", "free")
        .eq("planning_status", "published")
        .order("created_at", { ascending: false })
        .limit(24);
      if (!mounted) return;
      const shuffled = (data ?? []).sort(() => Math.random() - 0.5).slice(0, 4);
      setContents(shuffled);
      setContentsLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  const handleContentClick = (id: string, name: string) => {
    if (isLoggedIn) {
      navigate(`/content?open=${id}`);
      return;
    }
    setSelectedContentName(name);
    setAuthModalOpen(true);
  };

  return <div className="space-y-12 pb-12">
    <SEO
      title="LÍDER IA — Aprende, innova y gobierna la IA"
      description="Ecosistema de aprendizaje, innovación y gobernanza de IA. Rutas, diagnóstico, proyectos y herramientas para profesionales y empresas."
      path="/"
    />
      {/* Welcome Banner + Onboarding Checklist (only logged-in users without active cohort) */}
      <section className={showChecklist ? "grid lg:grid-cols-3 gap-6 items-stretch" : ""}>
        <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-background to-accent/10 p-4 md:p-12 border border-border ${showChecklist ? "lg:col-span-2" : ""}`}>
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent/10 rounded-full blur-3xl" />

          <div className="relative z-10">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              Ecosistema de aprendizaje, innovación, gestión y gobernanza de{" "}
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                Inteligencia Artificial
              </span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mb-6">
              Plataforma donde todo profesional puede aprender a usar IA e implementar proyectos reales.
              Y si eres empresa, puedes gestionar de forma integral la IA asegurando impacto y retorno a la inversión.
            </p>
            {!isLoggedIn && (
              <div className="flex flex-wrap gap-3">
                <Button size="lg" onClick={() => navigate("/auth?mode=signup")}>
                  Crear Cuenta Gratis
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate("/auth")}>
                  Ya tengo cuenta
                </Button>
              </div>
            )}
          </div>
        </div>

        {showChecklist && (
          <div className="lg:col-span-1">
            <OnboardingChecklistCard />
          </div>
        )}
      </section>
      {/* All learning routes */}
      {routesEnabled && (
      <section>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold">Rutas de aprendizaje</h2>
            <p className="text-muted-foreground">Todas las rutas de la plataforma, con acompañamiento o a tu ritmo</p>
          </div>
          <Button variant="ghost" onClick={() => navigate("/rutas")}>
            Ver todas las rutas <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
        {programsLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <Carousel opts={{ align: "start" }} className="w-full">
            <CarouselContent>
              {allPrograms.filter((p: any) => p.status === 'public').map((prog: any) => {
                const selfPaced = prog.modality === 'self_paced';
                return (
                <CarouselItem key={prog.id} className="basis-[85%] sm:basis-1/2 lg:basis-1/3">
                  <Card className="h-full overflow-hidden group hover:shadow-lg transition-all duration-300 cursor-pointer" onClick={() => navigate(`/rutas/${prog.slug || prog.id}`)}>
                    <div className="h-2" style={{ background: prog.color || 'hsl(var(--primary))' }} />
                    <CardContent className="p-5 flex flex-col h-full">
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Badge variant={selfPaced ? "outline" : "default"}>{selfPaced ? "Autoaprendizaje" : "Con acompañamiento"}</Badge>
                        {prog.level != null && <Badge variant="secondary">Nivel {prog.level}</Badge>}
                      </div>
                      <h3 className="font-semibold mb-2 line-clamp-2 group-hover:text-primary transition-colors">{prog.name}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{prog.description}</p>
                      {prog.duration && <span className="text-xs text-muted-foreground mt-auto">{prog.duration}</span>}
                    </CardContent>
                  </Card>
                </CarouselItem>
              );})}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex" />
            <CarouselNext className="hidden md:flex" />
          </Carousel>
        )}
      </section>
      )}

      {/* Featured Contents (public sample) */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold">Contenidos para aprender IA</h2>
            <p className="text-muted-foreground">Explora videos, lecturas y recursos del catálogo</p>
          </div>
          <Button variant="ghost" onClick={() => isLoggedIn ? navigate("/content") : setAuthModalOpen(true)}>
            Ver todo <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>

        {contentsLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : contents.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {contents.map((item, i) => (
              <Card
                key={item.id}
                className="group hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden relative"
                onClick={() => handleContentClick(item.id, item.name)}
              >
                <div className="aspect-video overflow-hidden bg-muted relative">
                  <img
                    src={item.thumbnail_url || contentFallbackThumbnails[i % contentFallbackThumbnails.length]}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {!isLoggedIn && (
                    <div className="absolute inset-0 bg-background/0 group-hover:bg-background/40 transition-colors flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity rounded-full bg-background/90 p-2 shadow-lg">
                        <Lock className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                  )}
                </div>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="gap-1">
                      {getContentIcon(item.content_type)}
                      {getTypeLabel(item.content_type)}
                    </Badge>
                    {item.duration && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {item.duration}
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold mb-1 line-clamp-2 group-hover:text-primary transition-colors">
                    {item.name}
                  </h3>
                  {item.category && (
                    <p className="text-xs text-muted-foreground line-clamp-1">{item.category}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-4">No hay contenidos disponibles</p>
        )}
      </section>

      <RegisterToViewModal
        open={authModalOpen}
        onOpenChange={setAuthModalOpen}
        contentName={selectedContentName}
      />

      {/* Quick Actions - CTAs */}

      {/* Latest Trends/Articles */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold">Últimas Tendencias</h2>
            <p className="text-muted-foreground">Mantente actualizado en el mundo de la IA</p>
          </div>
          <Button variant="ghost" onClick={() => navigate("/news")}>
            Ver todo <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>

        {newsLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : latestNews.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {latestNews.map((article, i) => (
              <Card key={article.id} className="group hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden" onClick={() => navigate(`/news/${article.id}`)}>
                <div className="aspect-video overflow-hidden">
                  <img src={article.image_url || newsFallbackImages[i % newsFallbackImages.length]} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                <CardContent className="p-4">
                  <Badge variant="outline" className="mb-2">{(article as any).category ?? 'Tendencias'}</Badge>
                  <h3 className="font-semibold mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                    {article.excerpt}
                  </p>
                  <span className="text-xs text-muted-foreground">
                    {article.published_at ? formatDistanceToNow(parseISO(article.published_at), { addSuffix: true, locale: es }) : ''}
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-4">No hay artículos disponibles</p>
        )}
      </section>

      <section className="grid md:grid-cols-2 gap-6">
        {/* Diagnostic CTA */}
        <Card className="group hover:shadow-lg transition-all duration-300 border-primary/20 hover:border-primary/50 cursor-pointer" onClick={() => navigate(isLoggedIn ? "/diagnostic" : "/auth?mode=signup")}>
          <CardContent className="p-6 flex items-start gap-4">
            <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center flex-shrink-0">
              <Stethoscope className="h-7 w-7 text-primary-foreground" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                Realiza tu Diagnóstico de IA
              </h3>
              <p className="text-muted-foreground mb-3">
                Descubre tu nivel de madurez en IA y recibe un roadmap personalizado para tu desarrollo.
              </p>
              <Button variant="link" className="p-0 h-auto text-primary">
                Comenzar diagnóstico <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Project CTA */}
        <Card className="group hover:shadow-lg transition-all duration-300 border-accent/20 hover:border-accent/50 cursor-pointer" onClick={() => navigate(isLoggedIn ? "/projects" : "/auth?mode=signup")}>
          <CardContent className="p-6 flex items-start gap-4">
            <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-accent to-accent/60 flex items-center justify-center flex-shrink-0">
              <FolderKanban className="h-7 w-7 text-accent-foreground" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold mb-2 group-hover:text-accent transition-colors">
                Crea tu Proyecto de IA
              </h3>
              <p className="text-muted-foreground mb-3">
                Obtén recomendaciones personalizadas de herramientas y estrategias para tu caso de uso.
              </p>
              <Button variant="link" className="p-0 h-auto text-accent">
                Crear proyecto <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Forum Preview */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold">Comunidad</h2>
            <p className="text-muted-foreground">Únete a las conversaciones más activas</p>
          </div>
          <Button variant="ghost" onClick={() => navigate("/forum")}>
            Ver todos <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>

        {forumLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : forumThreads.length > 0 ? (
          <div className="grid gap-3">
            {forumThreads.slice(0, 3).map((thread) => {
              const category = thread.category as keyof typeof THREAD_CATEGORIES;
              return (
                <Card key={thread.id} className="group hover:shadow-md transition-all cursor-pointer" onClick={() => navigate(`/forum?thread=${thread.id}`)}>
                  <CardContent className="p-3 md:p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
                      <div className="hidden md:flex h-10 w-10 rounded-full bg-muted items-center justify-center shrink-0">
                        <MessageCircle className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 max-w-full">
                        <h4 className="font-medium group-hover:text-primary transition-colors line-clamp-1">
                          {thread.title}
                        </h4>
                        <p className="text-sm text-muted-foreground truncate">
                          por {thread.author?.full_name || 'Anónimo'} • {thread.response_count} respuestas
                        </p>
                      </div>
                    </div>
                    {THREAD_CATEGORIES[category] && (
                      <Badge variant="secondary" className="hidden md:inline-flex">{THREAD_CATEGORIES[category].icon} {THREAD_CATEGORIES[category].label}</Badge>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="py-8 text-center">
              <MessageCircle className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">Aún no hay publicaciones en la comunidad</p>
              <Button size="sm" className="mt-3" onClick={() => navigate("/auth?mode=signup")}>
                Sé el primero en publicar
              </Button>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Impact Section */}
      <section>
        <div className="text-center mb-10">
          <Badge variant="outline" className="mb-4">
            <TrendingUp className="h-3 w-3 mr-1" />
            Nuestro Impacto
          </Badge>
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Transformando Profesionales y Empresas
          </h2>
        </div>

        <div className="mb-10">
          <ImpactStats />
        </div>

        <ImpactHighlights />
      </section>

      {/* Plans */}
      <section className="py-8 px-6 rounded-2xl bg-muted/30 border border-border">
        <div className="text-center mb-10">
          <Badge variant="outline" className="mb-4">Planes</Badge>
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Elige tu Plan
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Opciones flexibles para individuos y empresas.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {[{
           name: "Licencia Pro",
           price: proLabel,
           period: proPeriod,
          description: "Acceso a la plataforma y contenido",
          features: ["Acceso a todo el contenido", "Diagnóstico de madurez IA", "Comunidad de aprendizaje", "Herramientas y plantillas", "Proyecto real guiado"],
          highlighted: false,
          cta: "Comenzar",
          route: "/auth?mode=signup"
        }, {
          name: "Gratis",
          price: "$0",
          period: "/siempre",
          description: "Empieza a explorar la plataforma sin costo",
          features: ["Acceso a contenido gratuito", "Diagnóstico de madurez IA", "Comunidad de aprendizaje", "Asistente LIA (uso limitado)", "Agente IA para Diagnóstico de un proceso e identificación de un proyecto IA", "Tendencias IA", "Catálogo de Herramientas IA"],
          highlighted: false,
          cta: "Crear cuenta gratis",
          route: "/auth?mode=signup"
        }, {
          name: "Enterprise",
          price: "A medida",
          period: "",
          description: "Soluciones para equipos y empresas",
          features: ["Todo en Rutas", "Capacitación in-house", "Mentoría dedicada", "Dashboard de métricas", "Soporte prioritario 24/7"],
          highlighted: false,
          cta: "Contactar ventas",
          route: "/empresas"
        }].map((plan, i) => <Card key={i} className={`relative overflow-hidden ${plan.highlighted ? 'border-primary shadow-lg md:scale-105' : ''}`}>
              {plan.highlighted && <div className="absolute top-0 left-0 right-0 bg-primary text-primary-foreground text-center py-1 text-sm font-medium">
                  Más Popular
                </div>}
              <CardContent className={`p-5 ${plan.highlighted ? 'pt-9' : ''}`}>
                <h3 className="text-lg font-bold mb-2">{plan.name}</h3>
                <div className="mb-3">
                  <span className="text-3xl font-bold">{plan.price}</span>
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>
                <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                <ul className="space-y-2 mb-4">
                  {plan.features.map((feature, j) => <li key={j} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>)}
                </ul>
                <Button className="w-full" variant={plan.highlighted ? "default" : "outline"} onClick={() => navigate(plan.route)}>
                  {plan.cta}
                </Button>
              </CardContent>
            </Card>)}
        </div>

        <div className="text-center mt-6">
          <Button variant="ghost" onClick={() => navigate("/planes")}>
            Comparar planes en detalle <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* Certifications */}
      {routesEnabled && (
      <section>
        <div className="text-center mb-10">
          <Badge variant="outline" className="mb-4">Rutas Certificadas</Badge>
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Certifícate en Inteligencia Artificial
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">Nuestras certificaciones validan tus competencias y te posicionan a nivel profesional</p>
        </div>

        {programsLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {allPrograms
              .filter((p: any) => p.status === 'public' && p.modality !== 'self_paced')
              .slice(0, 3)
              .map((prog) => (
              <Card key={prog.id} className="overflow-hidden group hover:shadow-xl transition-all duration-300 cursor-pointer" onClick={() => navigate(`/rutas/${prog.slug || prog.id}`)}>
                <div className="h-2" style={{ background: `linear-gradient(to right, ${prog.color || 'hsl(var(--primary))'}, ${prog.color || 'hsl(var(--primary))'}88)` }} />
                <CardContent className="p-5">
                  <Badge variant="secondary" className="mb-3">Nivel {prog.level}</Badge>
                  <div className="flex items-center gap-2 mb-3">
                    <Award className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-bold">{prog.name}</h3>
                  </div>
                  <p className="text-muted-foreground text-sm mb-3 line-clamp-2">{prog.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{prog.duration}</span>
                    <Button variant="ghost" size="sm" className="group-hover:text-primary">
                      Ver más <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="text-center mt-6">
          <Button variant="outline" size="lg" onClick={() => navigate("/rutas")}>
            Ver todas las certificaciones <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </section>
      )}

      {/* Experts/Leaders */}
      <section className="py-8 px-6 rounded-2xl bg-muted/30 border border-border">
        <div className="text-center mb-10">
          <Badge variant="outline" className="mb-4">Expertos</Badge>
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Aprende de Líderes de la Industria
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Nuestro equipo de expertos combina experiencia y práctica en implementación de IA.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[{
          name: "Harold Combita",
          role: "CEO Vivetori - Creador LIDER IA",
          company: "+16 años en IA",
          photo: expertHarold
        }, {
          name: "Andrés Calderón",
          role: "Consultor IA",
          company: "",
          photo: expertAndres
        }, {
          name: "Keythleen Castellar",
          role: "Consultor IA",
          company: "",
          photo: expertKeythleen
        }, {
          name: "Julián Castaño",
          role: "CTO Vivetori - MSc Inteligencia Artificial",
          company: "",
          photo: expertJulian
        }].map((expert, i) => <Card key={i} className="group hover:shadow-lg transition-all duration-300 text-center">
              <CardContent className="p-5">
                <div className="h-24 w-24 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 mx-auto mb-3 overflow-hidden ring-2 ring-primary/20">
                  <img src={expert.photo} alt={expert.name} className="h-full w-full object-cover" />
                </div>
                <h3 className="font-semibold">{expert.name}</h3>
                <p className="text-primary text-sm">{expert.role}</p>
                <p className="text-muted-foreground text-sm">{expert.company}</p>
              </CardContent>
            </Card>)}
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsCarousel />

      {/* Final CTA - only for non-logged-in users */}
      {!isLoggedIn && (
        <section className="text-center py-8 px-6 rounded-2xl bg-gradient-to-br from-primary/10 via-background to-accent/10 border border-border">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            ¿Listo para iniciar tu primer proyecto de IA?
          </h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            Únete a cientos de profesionales que ya están transformando sus carreras y organizaciones.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" onClick={() => navigate("/auth?mode=signup")}>
              Crear Cuenta Gratis
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/auth")}>
              Ya tengo cuenta
            </Button>
          </div>
        </section>
      )}

    </div>;
};
export default PublicHomePage;